import { useCallback, useRef, useState } from "react";
import { ArrowsLeftRight } from "@phosphor-icons/react";

// Swipe-to-compare: the "before" photo sits on top, clipped to the handle position.
export const BeforeAfter = ({
  before,
  after,
  alt = "",
  className = "",
  testId = "before-after",
  start = 50,
  labels = true,
  onOpen,
}) => {
  const box = useRef(null);
  const [pos, setPos] = useState(start);
  const dragging = useRef(false);
  const moved = useRef(false);

  const place = useCallback((clientX) => {
    const r = box.current?.getBoundingClientRect();
    if (!r || r.width === 0) return;
    setPos(Math.max(2, Math.min(98, ((clientX - r.left) / r.width) * 100)));
  }, []);

  const down = (e) => {
    dragging.current = true;
    moved.current = false;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    place(e.clientX);
  };
  const move = (e) => {
    if (!dragging.current) return;
    moved.current = true;
    place(e.clientX);
  };
  const up = () => {
    if (dragging.current && !moved.current && onOpen) onOpen();
    dragging.current = false;
  };

  return (
    <div
      ref={box}
      data-testid={testId}
      className={`group/ba select-none overflow-hidden touch-pan-y ${className || "relative"}`}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      style={{ cursor: "ew-resize" }}
    >
      <img src={after} alt={alt} loading="lazy" draggable={false} className="absolute inset-0 h-full w-full object-cover" />
      <img
        src={before}
        alt=""
        loading="lazy"
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        data-testid={`${testId}-before`}
      />
      {labels && (
        <>
          <span className="s-micro pointer-events-none absolute left-3 top-3 rounded-full bg-black/55 px-2.5 py-1 !text-[9px] text-white backdrop-blur-sm">
            Before
          </span>
          <span className="s-micro pointer-events-none absolute right-3 top-3 rounded-full bg-[color:var(--s-gold-bright)] px-2.5 py-1 !text-[9px] text-[color:var(--s-ink)]">
            After
          </span>
        </>
      )}
      <span
        className="pointer-events-none absolute inset-y-0 w-px bg-white/90 shadow-[0_0_0_1px_rgba(0,0,0,0.25)]"
        style={{ left: `${pos}%` }}
      />
      <span
        className="pointer-events-none absolute top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-black/45 text-white backdrop-blur-sm transition-transform duration-300 group-hover/ba:scale-110"
        style={{ left: `${pos}%` }}
        data-testid={`${testId}-handle`}
      >
        <ArrowsLeftRight size={16} weight="bold" />
      </span>
      <input
        type="range"
        min="2"
        max="98"
        value={Math.round(pos)}
        onChange={(e) => setPos(Number(e.target.value))}
        aria-label="Compare before and after"
        data-testid={`${testId}-range`}
        className="sr-only"
      />
    </div>
  );
};
